import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt 
} from 'react-icons/fa';
import './SikkimDarjeeling.css';

const SikkimDarjeeling = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival at NJP / Bagdogra Airport & Transfer to Gangtok",
      points: [
        "Arrival at NJP Railway Station or Bagdogra Airport (IXB) where our representative will receive you.",
        "Proceed towards Gangtok, the capital city of Sikkim located at an altitude of approximately 1,437 meters above sea level.",
        "Enjoy the scenic drive through the Himalayan hills.",
        "Upon arrival:Hotel check-in,Evening free to explore MG Marg Market",
        "Overnight stay in Gangtok.Approx. Distance: 125 kmEstimated Travel Time: 5 hours"
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Tsomgo Lake & Baba Mandir Excursion",
      points: [
        "After breakfast, proceed for an excursion to Tsomgo Lake, located at an altitude of about 12,400 ft.",
        "Places to visit:Tsomgo Lake, Baba Harbhajan Singh Mandir",
        "After enjoying the stunning mountain views, return to Gangtok",
        "Evening free to explore MG Marg Market.",
        "Optional visit (extra cost): Nathula Pass (subject to permit availability)",
        "Overnight stay in Gangtok."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 3,
      title: "Gangtok to Pelling",
      points: [
        "After breakfast, check out from the hotel and travel towards Pelling, a peaceful hill town known for its magnificent views of the Kanchenjunga Range.",
        " Upon Arrival:Hotel check-in,Relax and enjoy the beautiful surroundings ",
        "Overnight stay in Pelling.",
        "Approx. Distance: 130 km",
        "Estimated Travel Time: 6 hours"
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 4,
      title: "Pelling Full Day Sightseeing",
      points: [
        "After breakfast, proceed for a full-day sightseeing tour of Pelling.",
        "Visit Darap Village and the serene Rimbi Waterfalls.",
        "Explore the sacred Khecheopalri Lake and the mighty Kanchenjunga Waterfalls.",
        "Visit the historic Pemayangtse Monastery and Rabdentse Ruins.",
        "Experience the thrill of Sang-Ngag Choling Sky Walk.",
        "Visit the New Helipad Ground for panoramic mountain views.",
        "Return to the hotel for a relaxing overnight stay in Pelling."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 5,
      title: "Pelling to Darjeeling Transfer",
      points: [
        "After breakfast, check out from the hotel and travel towards Darjeeling, one of India’s most famous hill stations.",
        "Experience a scenic journey of approx. 100 km (Estimated travel time: 4 hours).",
        "Upon arrival in Darjeeling, complete the hotel check-in formalities.",
        "Evening is free at leisure to explore the local market or the famous Mall Road.",
        "Stroll through the colonial streets and enjoy the mountain atmosphere.",
        "Delicious dinner and overnight stay in Darjeeling."
      ],
      icon: <FaPaperPlane />
    },
    {
      day: 6,
      title: "Darjeeling Full Day Sightseeing",
      points: [
        "Early morning visit to Tiger Hill to witness the famous sunrise over the Kanchenjunga Range (Subject to permit).",
        "Return to the hotel for a fresh breakfast before starting the local tour.",
        "Visit Himalayan Mountaineering Institute, Padmaja Naidu Zoological Park, and Tenzing Rock.",
        "Explore the Tibetan Refugee Self Help Centre and a scenic Tea Garden (outer view).",
        "Peaceful visit to the Japanese Temple, Ghoom Monastery, and the iconic Batasia Loop.",
        "Evening free for shopping at Mall Road or personal leisure activities.",
        "Overnight stay in Darjeeling."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 7,
      title: "Departure from Darjeeling to NJP / Bagdogra",
      points: [
        "Enjoy your final breakfast in the hills and complete the hotel check-out formalities.",
        "Smooth transfer to NJP Railway Station or Bagdogra Airport (IXB) for your onward journey.",
        "Scenic drive through the tea estates (Approx. 70 km | Estimated travel time: 3 hours).",
        "The tour concludes with beautiful and lasting memories of the Eastern Himalayas.",
        "Assistance with luggage and drop-off at the departure point."
      ],
      icon: <FaCheckCircle />
    },
   
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Sikkim & Darjeeling Himalayan Escape – <br />  Gangtok, Pelling & Darjeeling</h1>
          <p className="trip-duration">Duration: 7 nights & 8 days of Adventure</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Gangtok, Lachen, Pelling, Darjeeling</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Private & Personalized</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>Experiential Cultural Tour</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID (ORIGINAL STRUCTURE) */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/20897/BX1085422.jpg" alt="Sikkim Vertical" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/20898/FB1085452.jpg" alt="Tsomgo Middle" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/17619/KJ780267.jpg" alt="Darjeeling Small" />
          </div>
          <div className="grid-item small-h">
            <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/12606/HQ416785.jpg" alt="Pelling Small" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Excursion to the beautiful Tsomgo Lake in the Himalayas</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the sacred Baba Harbhajan Singh Mandir</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore scenic villages and waterfalls around Pelling</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the famous Pemayangtse Monastery and Sky Walk</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Experience sunrise views from Tiger Hill in Darjeeling</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit iconic attractions like Batasia Loop and Ghoom Monastery</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore the lively markets of MG Marg in Gangtok and Darjeeling Mall Road</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Enjoy breathtaking views of the Kanchenjunga mountain range</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS (LEFT-RIGHT) */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
        <div className="policy-box inclusion-box">
              <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
              <ul className="p-list">
                <li><FaHotel /> Accommodation in comfortable 3-star category hotels as per itinerary</li>
                <li><FaClock /> 6 Nights stay (2N Gangtok | 2N Pelling | 2N Darjeeling)</li>
                <li><FaStar /> Accommodation on double sharing basis</li>
                <li><FaUtensils /> Meal Plan: MAP (6 Breakfasts & 6 Dinners at hotels)</li>
                <li><FaMapMarkerAlt /> All sightseeing and transfers as mentioned in the itinerary</li>
                <li><FaBus /> Private non-AC vehicle (Innova / Xylo or similar) for the entire journey</li>
                <li><FaPaperPlane /> Pick-up from NJP Railway Station / Bagdogra Airport and drop back</li>
                <li><FaCheckCircle /> All Driver charges, fuel, toll taxes, parking, and permits</li>
                <li><FaShieldAlt /> Tour coordination and assistance throughout the journey</li>
              </ul>
            </div>
            <div className="policy-box exclusion-box">
              <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
              <ul className="p-list">
                <li><FaTimesCircle /> Airfare or train tickets for arrival and departure</li>
                <li><FaTimesCircle /> Entry fees for sightseeing places, monuments, or parks</li>
                <li><FaTimesCircle /> Optional visit to Nathula Pass (Permit and vehicle charges extra)</li>
                <li><FaTimesCircle /> Personal expenses such as tips, laundry, shopping, and telephone bills</li>
                <li><FaUtensils /> Lunch or any meals not explicitly mentioned in inclusions</li>
                <li><FaShieldAlt /> Travel insurance or any medical expenses</li>
                <li><FaTimesCircle /> Additional expenses due to natural calamities (landslides, road blocks, etc.)</li>
                <li><FaBus /> Additional sightseeing or extra usage of vehicle beyond the itinerary</li>
                <li><FaCheckCircle /> Any service or expense not mentioned in the inclusions section</li>
              </ul>
            </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">About This Journey</h2>
          <p className="about-text">The Sikkim & Darjeeling Himalayan Escape is a beautiful journey through the breathtaking landscapes of the Eastern Himalayas. This tour covers the charming hill towns of Gangtok, Pelling, and Darjeeling, offering travelers a perfect mix of natural beauty, cultural experiences, and spiritual sites.

From the serene waters of Tsomgo Lake and the sacred Baba Harbhajan Singh Temple to the scenic valleys of Pelling and the iconic viewpoints of Darjeeling, this tour allows travelers to explore some of the most picturesque destinations in India. Visitors will also witness stunning Himalayan views, visit ancient monasteries, and experience the unique charm of hill station markets and tea gardens.

This journey is ideal for travelers seeking a refreshing mountain getaway combined with cultural and scenic experiences.
</p>
        </motion.section>



{/* TERMS & CONDITIONS */}
<div className="accordion-item">
  <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
    <span>Terms & Conditions</span>
    <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
  </Link>
</div>

          {/* PRIVACY POLICY */}
          

{/* PRIVACY POLICY */}
<div className="accordion-item">
  {/* Link tag se ye click hote hi privacy page par le jayega */}
  <Link 
    to="/privacy-policy" 
    className="accordion-header" 
    style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
  >
    <span>Privacy Policy</span>
    {/* Arrow icon ko maine rotate kiya hai taaki wo 'Next' ka signal de */}
    <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
  </Link>
</div>



            {/* TRAVEL INFO & NOTES SECTION */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card">
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth journey.</p>
          </div>

          <div className="info-card docs-card">
            <h4 className="info-card-title"><FaShieldAlt /> Important Travel Information</h4>
            <p>Guests must carry the following documents during the tour:</p>
            <ul className="docs-list">
              <li>Original Passport / Voter ID / Driving License</li>
              <li>3 Passport-size photographs per traveler</li>
              <li>2 photocopies of ID proof for each traveler</li>
            </ul>
          </div>

          <div className="info-card extra-card">
            <h4 className="info-card-title"><FaStar /> Optional Activity (Extra Cost)</h4>
            <div className="price-tag-box">
              <span>Nathula Pass Permit:</span>
              <strong>Approx. ₹4500 – ₹5000 per vehicle</strong>
              <small>(Subject to permit availability & weather)</small>
            </div>
          </div>
        </motion.section>
          </div>
        </div>
    
  );
};

export default SikkimDarjeeling;